import { createMiddleware } from "@tanstack/react-start";

/**
 * Client-side middleware that attaches the current Supabase session's
 * access token as the `authorization: Bearer <token>` header so server
 * functions guarded by `requireSupabaseAuth` can authenticate the user.
 *
 * The Supabase client is imported dynamically so `@supabase/supabase-js`
 * (auth + realtime, ~150 KB) stays out of the app's main entry bundle and
 * is only fetched when a guarded server function is actually called.
 *
 * Use it together with `requireSupabaseAuth`:
 *   .middleware([attachSupabaseToken, requireSupabaseAuth])
 */
export const attachSupabaseToken = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    let token: string | undefined;
    try {
      const { supabase } = await import("./client");
      // Race getSession against a short timeout — in rare cases (network
      // stalls, refresh-token contention) getSession() never resolves, which
      // would hang every guarded server-fn call indefinitely.
      const result = await Promise.race([
        supabase.auth.getSession(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 2500)),
      ]);
      token = result?.data?.session?.access_token;
    } catch {
      // ignore — server will reject with 401 if no token
    }
    return next({
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  },
);
